import React, { FC, useEffect, useState, useCallback } from 'react';
import { Alert, Button, Modal } from "react-bootstrap";
import { FaExpandAlt } from "react-icons/fa";
import CategoriesService from "../../http/CategoriesService";
import Loading from "../Loading";

interface PropsModal {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    categoryId: number | string | null;
    refreshTable: () => void;
}

const CategoryModalDelete: FC<PropsModal> = ({ visible, setVisible, categoryId, refreshTable }) => {
    const [category, setCategory] = useState<any | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);

    const [loading, setLoading] = useState<boolean>(false);
    const [needsRefresh, setNeedsRefresh] = useState<boolean>(false);
    const [fullscreen, setFullscreen] = useState<boolean>(false);

    // Загрузка данных категории
    const fetchCategory = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await CategoriesService.query({
                includes: ["objects"],
                options: { where: { id: categoryId } }
            });

            if (data && data.length > 0) {
                const categoryData = data[0];
                if (categoryData.action === "DELETE") {
                    setError('Категория уже удалена');
                    setNeedsRefresh(true);
                } else if(categoryData.objects && categoryData.objects.length > 0) {
                    const arrObjects = [];
                    for (const object of categoryData.objects) {
                        if(object.action === "DELETE") continue;
                        arrObjects.push(`<b>${object.name}</b>`)
                    }
                    if(arrObjects.length > 0) {
                        setError(`<div class="h6 mb-1">Нельзя удалить!</div>Категория привязана к объекам:<br> ${arrObjects.join("<br>")}`);
                        setNeedsRefresh(true);
                    }
                }
                setCategory(categoryData);
            } else {
                setNeedsRefresh(true);
                setError("Категория не найдена");
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [categoryId]);

    useEffect(() => {
        if (visible) {
            void fetchCategory();
        }
    }, [visible, fetchCategory]);

    // Логика удаления (soft delete)
    const onCategoryDelete = async () => {
        setLoading(true);
        try {
            const { data } = await CategoriesService.update({
                values: { action: "DELETE" },
                options: { where: { id: category.id } }
            });

            if (data) {
                setSuccess("Категория удалена");
                setWarning(null);
                setNeedsRefresh(true);
            } else {
                setWarning("Не удалось удалить категорию");
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleHideModal = () => {
        if (needsRefresh) {
            refreshTable();
        }
        setVisible(false);
    };

    return (
        <Modal
            show={visible}
            size={"lg"}
            fullscreen={fullscreen ? true : "md-down"}
            scrollable={true}
            onHide={handleHideModal}
        >
            <div className={"modal-header"}>
                <h5 className={"mb-0"}>Удаление категории</h5>
                <div className={"d-flex gap-3 ai-center"}>
                    <button
                        type={"button"}
                        className={"btn-link border-0 bg-transparent"}
                        onClick={() => setFullscreen(!fullscreen)}
                    >
                        <FaExpandAlt />
                    </button>
                    <button type={"button"} className={"btn-close"} onClick={handleHideModal}></button>
                </div>
            </div>

            {loading ? (
                <div style={{ height: "200px" }} className={"d-flex ai-center jc-center"}>
                    <Loading />
                </div>
            ) : (
                <>
                    <Modal.Body>
                        {category ? (
                            <>
                                {success && (
                                    <Alert variant={"success"}>
                                        <div className={"fw-bold"}>{category.name}</div>
                                        {success}
                                    </Alert>
                                )}
                                {error && (
                                    <Alert variant={"danger"}>
                                        <div dangerouslySetInnerHTML={{ __html: error }} />
                                    </Alert>
                                )}
                                {!needsRefresh && (
                                    <Alert variant={"warning"}>
                                        <div className={"fw-bold"}>{category.name}</div>
                                        Категория будет удалена
                                    </Alert>
                                )}
                                {warning && <Alert variant={"warning"}>{warning}</Alert>}
                            </>
                        ) : (
                            <Alert variant={"warning"}>{error || "Категория не найдена"}</Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        {category && !needsRefresh && (
                            <Button variant={"danger"} type={"button"} onClick={onCategoryDelete}>
                                Удалить
                            </Button>
                        )}
                        <Button variant={"secondary"} onClick={handleHideModal}>
                            {needsRefresh ? "Закрыть" : "Отмена"}
                        </Button>
                    </Modal.Footer>
                </>
            )}
        </Modal>
    );
};

export default CategoryModalDelete;
